import { useState, useEffect } from 'react';

/** Local calendar date as YYYY-MM-DD — race days are local, not UTC. */
function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// ── Race-day overrides (ra-53j9) ──
//
// The one committee surface that writes LIVE. Everything else here is authored
// then published; a vessel swap at 11:00 for a 14:10 gun cannot wait for a
// publish cycle, and the alternative the club actually used — editing the
// series card and reverting it afterwards — is club-state mutation pretending
// to be an override.
//
// It is deliberately NOT gated behind the page's Edit mode: Edit flips the
// season matrix to the unpublished draft, which is the wrong thing to be
// looking at while running a race day. The live write is gated by its own
// confirm instead.

/** Pick the date an RO most likely means: today, else the next published one. */
export function defaultOverrideDate(dates, todayIso) {
  if (!dates || !dates.length) return '';
  if (dates.includes(todayIso)) return todayIso;
  const upcoming = dates.find((d) => d >= todayIso);
  return upcoming || dates[dates.length - 1];
}

/** Does this date carry a stored override of any kind (locations OR day)? */
export function hasStoredOverride(raceDates, date) {
  const stored = (raceDates || []).find((rd) => (rd.date || rd._id) === date);
  if (!stored) return false;
  const locs = stored.location_overrides || {};
  const day = typeof stored.day === 'string' ? stored.day.trim() : '';
  return Object.keys(locs).length > 0 || day !== '';
}

/** Seed the per-location form from the PUBLISHED doc — what the app shows now. */
function overrideFormFromDateDoc(dateDoc) {
  const locations = {};
  Object.entries(dateDoc?.locations || {}).forEach(([locId, loc]) => {
    locations[locId] = {
      vessel: loc?.committee_vessel?.name || '',
      vhf: loc?.vhf || '',
      name: loc?.name || '',
    };
  });
  return { day: dateDoc?.day || '', locations };
}

export default function RaceDayOverrideSection({ publishedDates, raceDates, loading, onApply, onClear }) {
  const dateIds = publishedDates.map((d) => d.date);
  const [date, setDate] = useState('');
  const [form, setForm] = useState({ day: '', locations: {} });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  // Default to today (or the next race day) once the published dates arrive,
  // and re-seed the form whenever the selected date's published doc changes —
  // including after an apply, so the fields show what is actually live.
  const dateDoc = publishedDates.find((d) => d.date === date) || null;
  useEffect(() => {
    if (!dateIds.length) return;
    setDate((prev) => (prev && dateIds.includes(prev) ? prev : defaultOverrideDate(dateIds, todayISO())));
    // dateIds is rebuilt each render; key the effect on its content, not identity.
  }, [dateIds.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setForm(overrideFormFromDateDoc(dateDoc)); setMsg(null); }, [dateDoc]);

  const stored = raceDates.find((rd) => (rd.date || rd._id) === date) || null;
  const storedOverrides = stored?.location_overrides || {};
  const overriddenLocs = Object.keys(storedOverrides);
  // A DAY-only override is a real override: handleApply can send `day` with no
  // locations at all. Gating "in force" and Clear on the locations map alone
  // would leave it stuck — durable, unclearable, and re-applied at every
  // Publish.
  const storedDay = typeof stored?.day === 'string' && stored.day.trim() ? stored.day.trim() : null;
  // One predicate for "is anything overridden here", shared with the date
  // picker's ● marker — two answers to that question is how a day-only
  // override became unclearable in the first place.
  const hasOverride = hasStoredOverride(raceDates, date);
  const locEntries = Object.entries(form.locations);

  function updateLoc(locId, patch) {
    setForm((prev) => ({ ...prev, locations: { ...prev.locations, [locId]: { ...prev.locations[locId], ...patch } } }));
  }

  async function handleApply() {
    // Send the FULL set of locations, not just the edited one: the store
    // replaces the stored override map wholesale, so a location the RO reverted
    // by clearing its vessel field must arrive as "no vessel" rather than be
    // absent and keep its old value at the next Publish.
    const locationOverrides = {};
    locEntries.forEach(([locId, loc]) => {
      const published = dateDoc?.locations?.[locId] || {};
      // Start from what is ALREADY stored for this location. The fields below
      // are diffed against the PUBLISHED doc, which already contains an earlier
      // override's value — so an RO who set VHF 69 last hour and now only
      // swaps the vessel would otherwise emit {committee_vessel} alone, the
      // wholesale replace would drop the VHF override, and the next Publish
      // would put the channel back to the series default with the new boat
      // still on it.
      const entry = { ...(storedOverrides[locId] || {}) };
      if (loc.vessel.trim()) entry.committee_vessel = { name: loc.vessel.trim() };
      else if (published.committee_vessel || entry.committee_vessel) entry.committee_vessel = null;
      if (loc.vhf.trim() && loc.vhf.trim() !== (published.vhf || '')) entry.vhf = loc.vhf.trim();
      if (loc.name.trim() && loc.name.trim() !== (published.name || '')) entry.name = loc.name.trim();
      if (Object.keys(entry).length) locationOverrides[locId] = entry;
    });
    const day = form.day.trim() !== (dateDoc?.day || '') ? form.day.trim() : '';
    if (!Object.keys(locationOverrides).length && !day) {
      setMsg({ type: 'error', text: 'Nothing changed — edit a vessel, VHF, start name or day first.' });
      return;
    }
    const names = Object.entries(locationOverrides)
      .map(([locId, o]) => `${locId}: ${o.committee_vessel === null ? 'vessel cleared' : (o.committee_vessel?.name || o.vhf || o.name)}`)
      .join('\n');
    if (!confirm(
      `Apply this override to ${date} NOW?\n\n${names || `day → ${day}`}\n\n`
      + 'This goes LIVE to every boat immediately — it does not wait for Publish.',
    )) return;
    setBusy(true);
    setMsg(null);
    try {
      const result = await onApply(date, { locationOverrides, day });
      setMsg(result.live
        ? { type: 'success', text: `Override live on ${date}.` }
        : { type: 'error', text: `Stored for ${date}, but that date has no published schedule yet — it will apply when you Publish.` });
    } catch (e) {
      setMsg({ type: 'error', text: `Could not apply: ${e.message}` });
    } finally {
      setBusy(false);
    }
  }

  async function handleClear() {
    if (!confirm(`Clear the race-day override for ${date}? The schedule goes back to the series default, live.`)) return;
    setBusy(true);
    setMsg(null);
    try {
      await onClear(date);
      setMsg({ type: 'success', text: `Override cleared for ${date}.` });
    } catch (e) {
      setMsg({ type: 'error', text: `Could not clear: ${e.message}` });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rt-section rt-pub-section">
      <h3>Race-day overrides <span style={{ fontSize: '.72rem', fontWeight: 400, color: '#c62828' }}>&#9679; writes live — no Publish needed</span></h3>
      <p style={{ fontSize: '.78rem', color: '#666', margin: '0 0 .6rem' }}>
        For a change on ONE race day: a different committee vessel, a different channel, a moved start.
        Applying writes the live schedule the app reads <strong>and</strong> records the override on the date, so the next
        <strong> Publish</strong> keeps it instead of reverting to the series default.
      </p>
      {loading ? <p>Loading published schedule…</p> : !dateIds.length ? (
        <p style={{ fontSize: '.85rem', color: '#888' }}>Nothing published yet — publish a schedule before overriding a date.</p>
      ) : (
        <>
          <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', flexWrap: 'wrap', marginBottom: '.6rem' }}>
            <label>Date:{' '}
              <select value={date} onChange={(e) => setDate(e.target.value)} className="rt-select">
                {dateIds.map((d) => <option key={d} value={d}>{d}{hasStoredOverride(raceDates, d) ? ' ●' : ''}</option>)}
              </select>
            </label>
            <label>Day label:{' '}
              <input value={form.day} onChange={(e) => setForm((p) => ({ ...p, day: e.target.value }))} className="rt-edit-input" style={{ width: '130px' }} placeholder="thursday" />
            </label>
          </div>
          {hasOverride && (
            <p style={{ fontSize: '.76rem', color: '#c62828', margin: '0 0 .5rem' }}>
              Override in force for: {overriddenLocs.length ? overriddenLocs.join(', ') : `day label (${storedDay})`}
            </p>
          )}
          {msg && <p className={msg.type === 'error' ? 'field-error' : 'rt-success-banner'} role="alert">{msg.text}</p>}
          {locEntries.length === 0 ? (
            <p style={{ fontSize: '.85rem', color: '#888' }}>This date has no start locations to override.</p>
          ) : (
            <table className="rt-table">
              <thead>
                <tr><th>Start location</th><th>Committee vessel</th><th>VHF</th><th>Display name</th></tr>
              </thead>
              <tbody>
                {locEntries.map(([locId, loc]) => (
                  <tr key={locId}>
                    <td><code>{locId}</code></td>
                    <td><input value={loc.vessel} onChange={(e) => updateLoc(locId, { vessel: e.target.value })} className="rt-edit-input" placeholder="e.g. Spirit of the Irish" style={{ width: '190px' }} /></td>
                    <td><input value={loc.vhf} onChange={(e) => updateLoc(locId, { vhf: e.target.value })} className="rt-edit-input" style={{ width: '60px' }} /></td>
                    <td><input value={loc.name} onChange={(e) => updateLoc(locId, { name: e.target.value })} className="rt-edit-input" style={{ width: '220px' }} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div style={{ display: 'flex', gap: '.5rem', marginTop: '.6rem' }}>
            <button className="rt-btn-save" onClick={handleApply} disabled={busy || !date}>{busy ? 'Applying…' : 'Apply live'}</button>
            <button className="rt-btn-cancel" onClick={handleClear} disabled={busy || !date || !hasOverride}>Clear override</button>
          </div>
        </>
      )}
    </section>
  );
}
